import { FormEvent, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2, Menu, User, X } from '@/components/ui/icons';
import { LOGO_FULL } from '@/lib/brand';
import { cn } from '@/lib/utils';
import { catalogService } from '@/services';
import { useSettingsStore } from '@/stores/settings.store';
import type { TcgCardSuggestion } from '@/types';
import { CartButton } from '@/components/ui/CartButton';
import { IconButton } from '@/components/ui/IconButton';
import { SearchBar } from '@/components/ui/SearchBar';
import { UserMenu } from '@/components/ui/UserMenu';
import { headerStyles } from './Header.styles';

export type HeaderNavItem = {
  to: string;
  label: string;
  isActive?: (pathname: string, search: string) => boolean;
  children?: HeaderNavItem[];
  showBadge?: boolean;
  /** Hide from desktop text row (shown via CartButton / UserMenu / mobile drawer). */
  slot?: 'cart' | 'profile' | 'login' | 'default';
};

export type HeaderProps = {
  appTitle: string;
  navItems: HeaderNavItem[];
  cartCount?: number;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  menuOpenLabel?: string;
  menuCloseLabel?: string;
  logoutLabel?: string;
  onLogout?: () => void;
  logoutPending?: boolean;
  isAuthenticated?: boolean;
};

export function Header({
  appTitle,
  navItems,
  cartCount = 0,
  searchPlaceholder = 'Search…',
  searchAriaLabel = 'Search',
  menuOpenLabel = 'Open menu',
  menuCloseLabel = 'Close menu',
  logoutLabel = 'Log out',
  onLogout,
  logoutPending = false,
  isAuthenticated = false,
}: HeaderProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const currency = useSettingsStore((s) => s.currency);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [openNavMenu, setOpenNavMenu] = useState<string | null>(null);
  const navMenuRef = useRef<HTMLDivElement>(null);

  const cartItem = navItems.find((i) => i.slot === 'cart' || i.showBadge);
  const profileItem = navItems.find((i) => i.slot === 'profile');
  const loginItem = navItems.find((i) => i.slot === 'login');
  const textLinks = navItems.filter(
    (i) => i.slot !== 'cart' && i.slot !== 'profile' && i.slot !== 'login' && !i.showBadge,
  );
  const trimmedSearch = search.trim();

  const {
    data: headerSuggestions = [],
    isLoading: isHeaderSuggestionsLoading,
  } = useQuery({
    queryKey: ['header-tcg-card-suggestions', trimmedSearch, currency],
    queryFn: () =>
      catalogService.searchTcgCardSuggestions({
        q: trimmedSearch,
        take: 8,
        currency,
      }),
    enabled: trimmedSearch.length >= 3,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!openNavMenu) return;
    const onDoc = (event: MouseEvent) => {
      if (!navMenuRef.current?.contains(event.target as Node)) {
        setOpenNavMenu(null);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenNavMenu(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [openNavMenu]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearch(params.get('q') ?? '');
  }, [location.search]);

  const goToTcgSearch = (q: string) => {
    const next = q.trim();
    navigate(next ? `/products?type=tcg&q=${encodeURIComponent(next)}` : '/products?type=tcg');
    setSearch(next);
    setSearchFocused(false);
    setMenuOpen(false);
  };

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    goToTcgSearch(search);
  };

  const getSuggestionMeta = (suggestion: TcgCardSuggestion): string => {
    const variant = suggestion.variants?.[0];
    return [variant?.setName, variant?.setRarity, variant?.setCode].filter(Boolean).join(' / ');
  };

  const shouldShowSuggestions =
    searchFocused && trimmedSearch.length >= 3;

  const searchField = (
    <form
      onSubmit={onSearchSubmit}
      className="relative w-full"
      onFocus={() => setSearchFocused(true)}
      onBlur={() => window.setTimeout(() => setSearchFocused(false), 120)}
    >
      <SearchBar
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchAriaLabel}
        showIcon={false}
        className={cn(
          'max-w-none border-brand-700/30 bg-surface text-brand-950 placeholder:text-brand-700/60',
          isHeaderSuggestionsLoading ? 'pr-10' : 'pr-4',
        )}
      />
      {isHeaderSuggestionsLoading ? (
        <span className="pointer-events-none absolute right-3 top-1/2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center text-brand-700">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        </span>
      ) : null}
      {shouldShowSuggestions ? (
        <div className={headerStyles.searchPanel}>
          <button
            type="submit"
            className={headerStyles.searchDirect}
            onMouseDown={(event) => event.preventDefault()}
          >
            {t('searchPanel.directSearch', { query: trimmedSearch })}
          </button>
          {isHeaderSuggestionsLoading ? (
            <div className={headerStyles.searchStatus}>{t('searchPanel.loading')}</div>
          ) : headerSuggestions.length > 0 ? (
            <div className="divide-y divide-brand-700/10">
              {headerSuggestions.map((suggestion) => {
                const meta = getSuggestionMeta(suggestion);
                return (
                  <button
                    key={suggestion.card.id}
                    type="button"
                    className={headerStyles.searchSuggestion}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => goToTcgSearch(suggestion.card.name)}
                  >
                    <span className="line-clamp-1 font-sans text-sm font-semibold text-brand-950">
                      {suggestion.card.name}
                    </span>
                    <span className="line-clamp-1 font-sans text-xs text-brand-700">
                      {meta || t('searchPanel.yugioh')}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className={headerStyles.searchStatus}>{t('searchPanel.noSuggestions')}</div>
          )}
        </div>
      ) : null}
    </form>
  );

  return (
    <header className={cn(headerStyles.root, 'relative')}>
      <div className={headerStyles.topBar}>
        <Link to="/" className={headerStyles.brand}>
          <img src={LOGO_FULL} alt={appTitle} className={headerStyles.logo} />
          <span className={headerStyles.brandText}>{appTitle}</span>
        </Link>

        <div className={headerStyles.searchWrap}>{searchField}</div>

        <div className={headerStyles.actions}>
          {cartItem ? (
            <CartButton
              to={cartItem.to}
              label={cartItem.label}
              count={cartCount}
              showLabel={false}
            />
          ) : null}

          {isAuthenticated && profileItem && onLogout ? (
            <UserMenu
              profileLabel={profileItem.label}
              logoutLabel={logoutLabel}
              profileTo={profileItem.to}
              showLabel={false}
              onLogout={onLogout}
              logoutPending={logoutPending}
            />
          ) : null}

          {!isAuthenticated && loginItem ? (
            <NavLink
              to={loginItem.to}
              className={({ isActive }) =>
                cn(headerStyles.actionLink, isActive && headerStyles.actionLinkActive)
              }
              aria-label={loginItem.label}
            >
              <User className="h-5 w-5" aria-hidden />
              <span className="hidden font-sans text-sm font-medium lg:inline">
                {loginItem.label}
              </span>
            </NavLink>
          ) : null}

          <IconButton
            type="button"
            variant="nav"
            size="sm"
            className={headerStyles.mobileMenu}
            aria-label={menuOpen ? menuCloseLabel : menuOpenLabel}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X className="h-5 w-5" aria-hidden /> : <Menu className="h-5 w-5" aria-hidden />}
          </IconButton>
        </div>
      </div>

      <div className={headerStyles.searchWrapMobile}>{searchField}</div>

      <nav className={headerStyles.navBar} aria-label="Main">
        <div ref={navMenuRef} className={headerStyles.navInner}>
          {textLinks.map((item) => {
            const isCustomActive = item.isActive?.(location.pathname, location.search);
            const hasChildren = item.children && item.children.length > 0;

            if (hasChildren) {
              const isOpen = openNavMenu === item.to;
              return (
                <div key={item.to} className="relative">
                  <button
                    type="button"
                    className={cn(headerStyles.link, isCustomActive && headerStyles.linkActive)}
                    aria-expanded={isOpen}
                    aria-haspopup="menu"
                    onClick={() => setOpenNavMenu((value) => (value === item.to ? null : item.to))}
                  >
                    {item.label}
                    <ChevronDown
                      className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
                      aria-hidden
                    />
                  </button>
                  {isOpen ? (
                    <div className={headerStyles.navDropdown} role="menu">
                      {item.children?.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          role="menuitem"
                          className={({ isActive }) =>
                            cn(
                              headerStyles.navDropdownLink,
                              (child.isActive?.(location.pathname, location.search) ?? isActive) &&
                                headerStyles.navDropdownLinkActive,
                            )
                          }
                          onClick={() => setOpenNavMenu(null)}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    headerStyles.link,
                    (isCustomActive ?? isActive) && headerStyles.linkActive,
                  )
                }
              >
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>

      {menuOpen ? (
        <div className={headerStyles.drawer}>
          {textLinks.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            if (hasChildren) {
              return (
                <div key={`m-${item.to}`} className="py-1">
                  <div className="px-3 py-1 font-sans text-xs font-semibold uppercase text-brand-400">
                    {item.label}
                  </div>
                  {item.children?.map((child) => (
                    <NavLink
                      key={`m-${child.to}`}
                      to={child.to}
                      className={({ isActive }) =>
                        cn(
                          headerStyles.drawerLink,
                          'pl-6',
                          (child.isActive?.(location.pathname, location.search) ?? isActive) &&
                            headerStyles.drawerLinkActive,
                        )
                      }
                      onClick={() => setMenuOpen(false)}
                    >
                      {child.label}
                    </NavLink>
                  ))}
                </div>
              );
            }

            return (
              <NavLink
                key={`m-${item.to}`}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    headerStyles.drawerLink,
                    (item.isActive?.(location.pathname, location.search) ?? isActive) &&
                      headerStyles.drawerLinkActive,
                  )
                }
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </NavLink>
            );
          })}
          {!isAuthenticated && loginItem ? (
            <NavLink
              to={loginItem.to}
              className={({ isActive }) =>
                cn(headerStyles.drawerLink, isActive && headerStyles.drawerLinkActive)
              }
              onClick={() => setMenuOpen(false)}
            >
              {loginItem.label}
            </NavLink>
          ) : null}
        </div>
      ) : null}
    </header>
  );
}
